import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { COMPANY_QUERY_KEYS } from '@/modules/company';

const ENDPOINT = '/api/v1/phone-lines';
const KEY = ['phone-lines'];

export type PhoneLineStatus = 'AWAITING_PAYMENT' | 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';

export interface PhoneLineCharge {
  id: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  dueDate: string;
  paidAt: string | null;
}

export interface PhoneLine {
  id: string;
  phoneNumber: string;
  country: string;
  status: PhoneLineStatus;
  monthlyPrice: number;
  paidThrough: string | null;
  contractedAt: string;
  unitId: string | null;
  unitName: string | null;
  provisioned: boolean;
  whatsappStatus: string | null;
  openCharge: PhoneLineCharge | null;
}

export interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
  monthlyPrice: number;
}

export function usePhoneLines() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiClient.get<{ data: PhoneLine[] }>(ENDPOINT),
  });
}

export function useAvailableNumbers(params: { areaCode: string; enabled: boolean }) {
  const areaCode = params.areaCode.replace(/\D/g, '');

  return useQuery({
    queryKey: [...KEY, 'available', areaCode],
    enabled: params.enabled,
    queryFn: () =>
      apiClient.get<{ data: AvailableNumber[] }>(
        `${ENDPOINT}/available?country=BR&limit=8${areaCode ? `&areaCode=${areaCode}` : ''}`,
      ),
  });
}

function usePhoneLineMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.units });
    },
  });
}

export function useContractLine() {
  return usePhoneLineMutation((input: { phoneNumber: string; unitId?: string }) =>
    apiClient.post<PhoneLine>(ENDPOINT, { ...input, country: 'BR' }),
  );
}

export function useConnectLine() {
  return usePhoneLineMutation(({ id, unitId }: { id: string; unitId: string }) =>
    apiClient.post<PhoneLine>(`${ENDPOINT}/${id}/connect`, { unitId }),
  );
}

export function useCancelLine() {
  return usePhoneLineMutation((id: string) => apiClient.delete<void>(`${ENDPOINT}/${id}`));
}
